import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Upload,
  Select,
  Space,
  List,
  message,
  Popconfirm,
  Tag,
  Alert,
  Spin,
  Tooltip,
  Input,
  Row,
  Col,
  Badge
} from 'antd';
import {
  UploadOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import {
  uploadInvoiceDocument,
  getInvoiceDocuments,
  deleteInvoiceDocument,
  downloadPDFFromStorage,
  downloadPDFBlobFromStorage
} from '../../firebase/pdfStorageService';
import {
  INVOICE_DOCUMENT_TYPES,
  getAllDocumentTypes,
  getDocumentType,
  getDocumentStatusColor,
  getDocumentStatusText
} from '../Shared/invoiceDocumentTypes';
import moment from 'moment';
import './InvoiceDocumentManager.css';

const { Option } = Select;

/**
 * InvoiceDocumentManager Component
 * 
 * Manages invoice-related documents:
 * - Finalised Invoice
 * - Delivery Order (DO)
 * - Purchase Order (PO)
 * - Rental Terms (RT)
 */
const InvoiceDocumentManager = ({
  invoiceId,
  invoiceNumber,
  readOnly = false,
  compact = false
}) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [documents, setDocuments] = useState({});
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [clientPONumber, setClientPONumber] = useState('');
  const [uploadFile, setUploadFile] = useState(null);

  // Load documents on mount
  useEffect(() => {
    if (invoiceId) {
      loadDocuments();
    }
  }, [invoiceId]);

  // Load all invoice documents
  const loadDocuments = async () => {
    if (!invoiceId) return;

    setLoading(true);
    try {
      const attachments = await getInvoiceDocuments(invoiceId);
      setDocuments(attachments);
    } catch (error) {
      console.error('Error loading invoice documents:', error);
      message.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (file) => {
    if (!invoiceId || !invoiceNumber) {
      message.error('Invoice must be saved before uploading documents');
      return false;
    }

    if (!selectedDocType) {
      message.error('Please select a document type first');
      return false;
    }

    if (file.type !== 'application/pdf') {
      message.error('Please upload a PDF file');
      return false;
    }

    setUploadFile(file);
    
    // Trigger upload immediately
    handleUpload(file);
    
    return false; // Prevent default upload behavior
  };

  // Handle document upload
  const handleUpload = async (file) => {
    if (!file || !selectedDocType) return;

    setUploading(true);
    try {
      const docType = getDocumentType(selectedDocType);
      
      // Prepare additional metadata
      const metadata = {};
      if (selectedDocType === 'purchaseOrder' && clientPONumber) {
        metadata.clientPONumber = clientPONumber;
      }

      // Upload document
      const result = await uploadInvoiceDocument(
        file,
        invoiceId,
        invoiceNumber,
        selectedDocType,
        metadata
      );

      message.success(`${docType.label} uploaded successfully`);
      
      // Reset form
      setSelectedDocType(null);
      setClientPONumber('');
      setUploadFile(null);
      
      // Reload documents
      await loadDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      message.error(error.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // Handle document download - Use cloud function to bypass CORS completely
  const handleDownload = async (docTypeKey) => {
    const doc = documents[docTypeKey];
    if (!doc) return;

    setDownloading(prev => ({ ...prev, [docTypeKey]: true }));
    
    try {
      // Download PDF blob via Cloud Function (bypasses CORS completely)
      const { blob, fileName } = await downloadPDFBlobFromStorage(doc.storagePath);
      
      // Create object URL from blob (same-origin, allows download attribute to work)
      const blobURL = window.URL.createObjectURL(blob);
      
      // Create temporary link to trigger download
      const link = window.document.createElement('a');
      link.href = blobURL;
      link.download = fileName || doc.fileName;
      
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      
      // Clean up object URL to free memory
      window.URL.revokeObjectURL(blobURL);
      
      message.success('File downloaded successfully');
    } catch (error) {
      console.error('Error downloading document:', error);
      message.error('Failed to download document');
    } finally {
      setDownloading(prev => ({ ...prev, [docTypeKey]: false }));
    }
  };

  // Handle document preview
  const handlePreview = async (docTypeKey) => {
    const document = documents[docTypeKey];
    if (!document) return;

    try {
      const downloadURL = await downloadPDFFromStorage(document.storagePath);
      window.open(downloadURL, '_blank');
    } catch (error) {
      console.error('Error previewing document:', error);
      message.error('Failed to preview document');
    }
  };

  // Handle document deletion
  const handleDelete = async (docTypeKey) => {
    const document = documents[docTypeKey];
    if (!document) return;

    try {
      await deleteInvoiceDocument(invoiceId, docTypeKey, document.storagePath);
      message.success('Document deleted successfully');
      
      // Reload documents
      await loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      message.error('Failed to delete document');
    }
  };

  // Get document upload status
  const getUploadStatus = (docTypeKey) => {
    const docType = getDocumentType(docTypeKey);
    const isUploaded = !!documents[docTypeKey];
    
    return {
      isUploaded,
      color: getDocumentStatusColor(isUploaded, docType.required),
      text: getDocumentStatusText(isUploaded, docType.required)
    };
  };

  // Compact mode rendering
  if (compact) {
    const allTypes = getAllDocumentTypes();
    const uploadedCount = allTypes.filter(type => documents[type.key]).length;
    const totalCount = allTypes.length;

    return (
      <div className="invoice-document-manager-compact">
        {loading ? (
          <Spin size="small" />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Badge count={uploadedCount} showZero>
              <Tag icon={<FilePdfOutlined />} color="blue">
                {uploadedCount}/{totalCount} Documents
              </Tag>
            </Badge>
            
            {allTypes.map(type => {
              const status = getUploadStatus(type.key);
              return (
                <div key={type.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px' }}>{type.icon}</span>
                  <Tag color={status.color} style={{ margin: 0 }}>
                    {type.label}
                  </Tag>
                  {status.isUploaded && (
                    <Space size="small">
                      <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handlePreview(type.key)}
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownload(type.key)}
                        loading={downloading[type.key]}
                      />
                    </Space>
                  )}
                </div>
              );
            })}
          </Space>
        )}
      </div>
    );
  }

  // Full mode rendering
  const allTypes = getAllDocumentTypes();

  return (
    <Card
      className="invoice-document-manager"
      title={
        <Space>
          <FilePdfOutlined />
          <span>Invoice Documents</span>
          <Tag color="blue">
            {invoiceNumber || 'New Invoice'}
          </Tag>
        </Space>
      }
      loading={loading}
    >
      {!invoiceId ? (
        <Alert
          message="Save Required"
          description="Please save the invoice before uploading documents."
          type="warning"
          showIcon
          icon={<InfoCircleOutlined />}
        />
      ) : (
        <>
          {/* Upload Section */}
          {!readOnly && (
            <div className="upload-section" style={{ marginBottom: 24 }}>
              <Alert
                message="Upload Invoice Documents"
                description="Select the document type and upload the PDF file. Each document type can have one file."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select document type"
                    value={selectedDocType}
                    onChange={setSelectedDocType}
                    disabled={uploading}
                  >
                    {allTypes.map(type => (
                      <Option key={type.key} value={type.key}>
                        <Space>
                          <span>{type.icon}</span>
                          <span>{type.label}</span>
                          {documents[type.key] && (
                            <Tag color="success" style={{ marginLeft: 8 }}>
                              Uploaded
                            </Tag>
                          )}
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </Col>

                {selectedDocType === 'purchaseOrder' && (
                  <Col xs={24} md={12}>
                    <Input
                      placeholder="Client PO Number (optional)"
                      value={clientPONumber}
                      onChange={(e) => setClientPONumber(e.target.value)}
                      disabled={uploading}
                      prefix="PO:"
                    />
                  </Col>
                )}

                <Col xs={24} md={selectedDocType === 'purchaseOrder' ? 24 : 12}>
                  <Upload
                    beforeUpload={handleFileSelect}
                    showUploadList={false}
                    accept=".pdf"
                    disabled={!selectedDocType || uploading}
                  >
                    <Button
                      type="primary"
                      icon={<UploadOutlined />}
                      loading={uploading}
                      disabled={!selectedDocType}
                      block
                    >
                      {uploading ? 'Uploading...' : 'Choose PDF File'}
                    </Button>
                  </Upload>
                </Col>
              </Row>

              {selectedDocType && (
                <Alert
                  message={
                    <span>
                      Uploading: <strong>{getDocumentType(selectedDocType).label}</strong>
                      {documents[selectedDocType] && (
                        <span style={{ color: '#ff4d4f' }}>
                          {' '}(This will replace the existing file)
                        </span>
                      )}
                    </span>
                  }
                  type="warning"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
            </div>
          )}

          {/* Documents List */}
          <div className="documents-list">
            <h4 style={{ marginBottom: 16 }}>Uploaded Documents</h4>
            <List
              dataSource={allTypes}
              renderItem={(type) => {
                const document = documents[type.key];
                const status = getUploadStatus(type.key);

                return (
                  <List.Item
                    key={type.key}
                    actions={
                      document && !readOnly
                        ? [
                            <Tooltip title="Preview">
                              <Button
                                type="text"
                                icon={<EyeOutlined />}
                                onClick={() => handlePreview(type.key)}
                              />
                            </Tooltip>,
                            <Tooltip title="Download">
                              <Button
                                type="text"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownload(type.key)}
                                loading={downloading[type.key]}
                              />
                            </Tooltip>,
                            <Popconfirm
                              title="Delete this document?"
                              description="This action cannot be undone."
                              onConfirm={() => handleDelete(type.key)}
                              okText="Yes"
                              cancelText="No"
                              okButtonProps={{ danger: true }}
                            >
                              <Tooltip title="Delete">
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                />
                              </Tooltip>
                            </Popconfirm>
                          ]
                        : document
                        ? [
                            <Tooltip title="Preview">
                              <Button
                                type="text"
                                icon={<EyeOutlined />}
                                onClick={() => handlePreview(type.key)}
                              />
                            </Tooltip>,
                            <Tooltip title="Download">
                              <Button
                                type="text"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownload(type.key)}
                                loading={downloading[type.key]}
                              />
                            </Tooltip>
                          ]
                        : []
                    }
                  >
                    <List.Item.Meta
                      avatar={
                        <span style={{ fontSize: '24px' }}>{type.icon}</span>
                      }
                      title={
                        <Space>
                          <span>{type.label}</span>
                          {type.required && (
                            <Tag color="red" style={{ fontSize: '10px' }}>
                              REQUIRED
                            </Tag>
                          )}
                          <Tag color={status.color}>
                            {status.isUploaded ? (
                              <CheckCircleOutlined />
                            ) : (
                              <CloseCircleOutlined />
                            )}{' '}
                            {status.text}
                          </Tag>
                        </Space>
                      }
                      description={
                        document ? (
                          <Space direction="vertical" size="small">
                            <div>
                              <strong>File:</strong> {document.fileName}
                            </div>
                            <div>
                              <strong>Uploaded:</strong>{' '}
                              {moment(document.uploadedAt.toDate ? document.uploadedAt.toDate() : document.uploadedAt).format(
                                'DD/MM/YYYY HH:mm'
                              )}
                            </div>
                            <div>
                              <strong>By:</strong> {document.uploadedBy}
                            </div>
                            <div>
                              <strong>Size:</strong>{' '}
                              {(document.fileSize / 1024).toFixed(2)} KB
                            </div>
                            {document.clientPONumber && (
                              <div>
                                <strong>Client PO:</strong> {document.clientPONumber}
                              </div>
                            )}
                          </Space>
                        ) : (
                          <span style={{ color: '#999' }}>
                            {type.description}
                          </span>
                        )
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </div>
        </>
      )}
    </Card>
  );
};

export default InvoiceDocumentManager;
